import { useTranslation } from 'react-i18next';
import {
  ArrayNode,
  getData,
  JsonSchema,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { FormLabel } from '../../../components/formlabel/FormLabel';
import {
  MultiSelect,
  MultiSelectOption,
} from '../../../components/selects/MultiSelect';
import { SingleSelect } from '../../../components/selects/SingleSelect';
import { MultiSelectWidgetOptions } from '../selectWidgetOptions';
import { resolvePlaceholder } from '../widgetUtils';

function isStringItemsSchema(items: unknown): items is StringItemsSchema {
  return (
    items != null &&
    typeof items === 'object' &&
    'type' in items &&
    items.type === 'string'
  );
}

type StringItemsSchema = Omit<JsonSchema, 'enum'> & {
  type: 'string';
  enum?: string[];
  options?: {
    enum?: string[];
  };
};

export type SelectOptions = MultiSelectWidgetOptions;

export const MultiSelectWidget = widget<
  ArrayNode<MultiSelectWidgetOptions>,
  (string | null)[] | null
>(({ node, options, setValue }) => {
  const { t } = useTranslation();
  const itemsSchema = node.schema.items as StringItemsSchema; // we tested this in plugin
  const enumValues = itemsSchema.enum ?? [];
  const titles = itemsSchema.options?.enum ?? [];
  const data = getData(node) as string[];
  const placeholder = resolvePlaceholder(
    options.placeholder,
    t('app.forms.placeholders.select')
  );

  return (
    <FormField
      widgetType="select"
      node={node}
      options={options}
      tableLayout={options.tableLayout}
    >
      {options.showTitle !== false && (
        <FormLabel
          description={options.description}
          required={options.required}
          disabled={options.disabled}
          label={options.title}
          error={options.showErrors !== false && node.errors.length > 0}
        />
      )}

      {node.schema.maxItems === 1 ? (
        <SingleSelect
          data={enumValues.map((value, index) => ({
            label: titles[index] ?? value,
            value,
          }))}
          placeholder={placeholder}
          disabled={options.disabled}
          readOnly={options.readOnly}
          loading={options.loading}
          value={data[0] ?? null}
          onChange={(value) => setValue(value != null ? [value] : [])}
        />
      ) : (
        <MultiSelect
          data={enumValues.map((value, index) => ({
            label: titles[index] ?? value,
            value,
          }))}
          placeholder={placeholder}
          disabled={options.disabled}
          readOnly={options.readOnly}
          loading={options.loading}
          defaultValue={enumValues.length === 0 ? [] : data}
          onCommit={setValue}
          sortCallback={options.sortCallback}
        />
      )}
    </FormField>
  );
});

export const MultiSelectWidgetPlugin: WidgetPlugin = {
  id: 'multi-select-widget',
  use: (node) =>
    node.schema.type === 'array' && isStringItemsSchema(node.schema.items),
  Widget: MultiSelectWidget,
};

export const MultiSelectWidgetPluginWrapper = (
  sortCallback?: (a: MultiSelectOption, b: MultiSelectOption) => number
): WidgetPlugin => {
  return {
    id: 'multi-select-widget',
    use: (node, options) => {
      if (options && sortCallback) {
        options.sortCallback = sortCallback;
      }
      return (
        node.schema.type === 'array' && isStringItemsSchema(node.schema.items)
      );
    },
    Widget: MultiSelectWidget,
  };
};
